<template>
  <div class="new-home-account-status">
    <div class="home-account-status-card" data-testid="home-account-status-card">
      <div class="home-account-status-card__content">
        <div class="home-account-status-card__header">
          <HeadingDisplay size="extra-small" :title="title" />
          <BaseText
            class="home-account-status-card__description"
            size="p5"
            :color="NebraskaColors.textSecondary"
          >
            {{ description }}
          </BaseText>
          <div
            v-if="stepIndex != null && stepLength != null"
            class="home-account-status-card__stepper"
          >
            <Stepper :steps-count="stepLength" :current-step="stepIndex" />
          </div>
        </div>
        <div class="home-account-status-card__bottom">
          <div
            class="home-account-status-card__bottom-row"
            role="button"
            tabindex="0"
            @click="handleClick"
            @keydown.space.enter.prevent="handleClick"
          >
            <BaseText
              class="home-account-status-card__button-label"
              size="p5"
              :style="{ fontWeight: 600 }"
            >
              {{ buttonLabel || '' }}
            </BaseText>
            <div
              v-if="stepIndex != null || stepLength != null"
              class="home-account-status-card__step-info"
            >
              <BaseText size="p6" :color="NebraskaColors.elementSecondary">
                Etapa {{ (stepIndex) }} de {{ stepLength }}
              </BaseText>
              <BaseIcon
                icon="ic_arrow_right"
                :colors="[NebraskaColors.elementSecondary]"
                width="16"
                height="16"
              />
            </div>
            <BaseIcon
              v-else
              icon="ic_arrow_right"
              :colors="[NebraskaColors.elementSecondary]"
              width="16"
              height="16"
            />
          </div>
        </div>
      </div>
    </div>
    <section>
      <div class="home-hero">
        <HomeHeroActions
          :is-overdue="false"
          :is-loading="false"
          :hero-buttons="buttons"
          data-testid="home-hero-actions"
        />
      </div>
      <FadeTransition>
        <div id="home-hero-summary">
          <HomeHeroSummary
            :is-loading="false"
            :balance="'R$ 0,00'"
            :has-error="false"
            @click="() => {}"
          />
        </div>
      </FadeTransition>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { BaseText, HeadingDisplay, BaseIcon } from '@warrenbrasil/nebraska-web';
import { AccountStates } from '@/modules/common/services/http/account';
import Stepper from './components/Stepper.vue';
import { ButtonAction, HomeBalanceHero } from '../../services/balances/types';
import HomeHeroActions from '../HomeHero/components/HomeHeroActions.vue';
import HomeHeroSummary from '../HomeHero/components/HomeHeroSummary.vue';
import { Money } from '@/modules/common/components/Money';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

@Component({
  components: {
    BaseText,
    HeadingDisplay,
    BaseIcon,
    Stepper,
    HomeHeroActions,
    HomeHeroSummary,
    Money,
    FadeTransition
  }
})
export default class NewHomeAccountStatus extends Vue {
  public readonly NebraskaColors = NebraskaColors;

  @Prop({ type: Object, required: true })
  public accountStatusResponse!: AccountStates;

  @Prop({ type: Function, required: false })
  public buttonAction?: () => void;

  get title(): string {
    return this.accountStatusResponse?.accountStatus?.title || '';
  }

  get description(): string {
    return this.accountStatusResponse?.accountStatus?.description || '';
  }

  get buttonLabel(): string | undefined {
    return this.accountStatusResponse?.accountStatus?.button?.label;
  }

  get stepTitle(): string | undefined {
    return this.accountStatusResponse?.accountStatus?.step?.title;
  }

  get stepIndex(): number | undefined {
    return this.accountStatusResponse?.accountStatus?.step?.index;
  }

  get stepLength(): number | undefined {
    return this.accountStatusResponse?.accountStatus?.step?.length;
  }

  get buttons(): HomeBalanceHero {
    return {
      accountBalance: '',
      buttons: [
        {
          label: 'Investir',
          icon: 'BA0020',
          isActive: true,
          action: ButtonAction.Invest
        },
        {
          label: 'Depositar',
          icon: 'EA0021',
          isActive: false,
          action: ButtonAction.Deposit
        },
        {
          label: 'Transferir',
          icon: 'AA0060',
          isActive: false,
          action: ButtonAction.Transfer
        }
      ]
    };
  }

  get hasStepInfo(): boolean {
    return this.stepIndex != null && this.stepLength != null;
  }

  get stepperProps(): { stepsCount: number; currentStep: number } {
    return {
      stepsCount: this.stepLength!,
      currentStep: this.stepIndex!
    };
  }

  public handleClick() {
    if (this.buttonAction) {
      this.buttonAction();
    }
  }
}
</script>

<style lang="less" scoped>
@max-width: 1008px;

.home-account-status-card {
  background-color: @background-secondary;
  border-radius: @size-radius-x300;
  width: 100%;
  max-width: @max-width;
  margin: 0 auto @size-spacing-x600 auto;

  &__content {
    display: flex;
    flex-direction: column;
    min-height: fit-content;
  }

  &__header {
    display: flex;
    flex-direction: column;
    padding-top: @size-spacing-x500;
    padding-left: @size-spacing-x500;
    padding-right: @size-spacing-x500;
  }

  &__title {
    color: @text-primary;
    margin-bottom: @size-spacing-x300;
  }

  &__description {
    margin-bottom: @size-spacing-x400;
  }

  &__stepper {
    margin-bottom: @size-spacing-x400;
  }

  &__bottom {
    padding-bottom: @size-spacing-x400;
  }

  &__bottom-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 @size-spacing-x500;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.6;
    }
  }

  &__button-label {
    flex: 1;
    color: @text-primary;
  }

  &__step-info {
    display: flex;
    align-items: center;
    gap: @size-spacing-x300;
    margin-left: @size-spacing-x300;
  }
}

.home-hero {
  max-width: @max-width;
  margin: @size-spacing-x600 auto;
}

.home-hero-summary {
  max-width: @max-width;
  margin: @size-spacing-x600 auto;
}
</style>
